import { useCallback, useRef, useState } from 'react';
import { useUIStore } from '@stores/useUIStore';

/**
 * Vertical drag handle between the sidebar and the viewport. Dragging
 * left/right adjusts the sidebar width percentage in the store. Visual:
 * a subtle 2px line that widens + highlights on hover and while active.
 */
export default function ResizeHandle() {
  const setSidebarWidthPct = useUIStore((s) => s.setSidebarWidthPct);
  const [active, setActive] = useState(false);
  const [hovered, setHovered] = useState(false);
  const startXRef = useRef(0);
  const startPctRef = useRef(0);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setActive(true);
      startXRef.current = e.clientX;
      startPctRef.current = useUIStore.getState().sidebarWidthPct;

      const onMove = (mv: PointerEvent) => {
        const dx = mv.clientX - startXRef.current;
        const deltaPct = (dx / window.innerWidth) * 100;
        setSidebarWidthPct(startPctRef.current + deltaPct);
      };

      const onUp = () => {
        setActive(false);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [setSidebarWidthPct],
  );

  const isHighlighted = active || hovered;

  return (
    <div
      onPointerDown={handlePointerDown}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      tabIndex={0}
      onKeyDown={(e) => {
        const store = useUIStore.getState();
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          store.setSidebarWidthPct(store.sidebarWidthPct - 1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          store.setSidebarWidthPct(store.sidebarWidthPct + 1);
        }
      }}
      style={{
        width: isHighlighted ? 6 : 4,
        flexShrink: 0,
        cursor: 'col-resize',
        background: isHighlighted
          ? 'rgba(112, 148, 36, 0.4)'
          : 'var(--border-dim)',
        transition: 'width 0.15s ease-out, background 0.15s ease-out',
        position: 'relative',
        zIndex: 10,
        touchAction: 'none',
      }}
    >
      {/* Center line indicator */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 2,
          height: 32,
          borderRadius: 1,
          background: isHighlighted
            ? 'var(--accent-green)'
            : 'var(--border-mid)',
          transition: 'background 0.15s ease-out',
        }}
      />
    </div>
  );
}
